import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { NotificationToaster } from '../../utils/NotificationToaster';
import { GET_PREDICATE_DEVICES_GQL } from '../../gql/queries/summary';
import { 
  REMOVE_PRODUCT_FROM_IDEA, 
  CONFIRM_PRODUCT_STATUS_GQL, 
  UNCONFIRM_PRODUCT_STATUS_GQL 
} from '../../gql/mutations/products';

// Styled components for a modern UI
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: 0,
  borderRadius: 0,
  boxShadow: 'none',
  height: '100%',
  width: '100%',
  overflow: 'auto'
}));

const HeaderCell = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  fontWeight: 'bold',
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(0.5),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: '1px solid #bdbdbd',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
  position: 'relative'
}));

const DataCell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderBottom: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
  whiteSpace: 'normal',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  }
}));

const StatusButton = styled(Button)(({ theme, status }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(0.25, 1.5),
  fontSize: '0.75rem',
  minWidth: '80px',
  height: '28px',
  backgroundColor: status === 'CONFIRMED' ? '#038C5A' : '#F0C577',
  color: status === 'CONFIRMED' ? '#fff' : '#000',
  '&:hover': {
    backgroundColor: status === 'CONFIRMED' ? '#026e45' : '#e5b55f',
  }
}));

const PredicatesGrid = () => {
  const dispatch = useDispatch();
  const ideaId = useSelector((state) => state?.explore?.idea?.id);
  
  const [predicateDevices, setPredicateDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // GraphQL queries and mutations
  const [getPredicateDevices] = useLazyQuery(GET_PREDICATE_DEVICES_GQL);
  const [removePredicateDevice] = useMutation(REMOVE_PRODUCT_FROM_IDEA);
  const [confirmPredicateDevice] = useMutation(CONFIRM_PRODUCT_STATUS_GQL);
  const [unconfirmPredicateDevice] = useMutation(UNCONFIRM_PRODUCT_STATUS_GQL);

  // Handle status change (confirm/unconfirm)
  const handleStatusChange = (productId, productStatus) => {
    if (productStatus === 'NEW_CHANGES') {
      confirmPredicateDevice({
        variables: {
          ideaId: ideaId,
          productId: productId
        }
      })
        .then((res) => {
          const updatedPredicateDevices = predicateDevices.map((obj) => {
            return obj?.id === productId
              ? { ...obj, status: res?.data?.confirmProductInIdea?.status }
              : obj;
          });

          setPredicateDevices(updatedPredicateDevices);
          dispatch({
            type: 'PREDICATE_DEVICES_SUCCESS',
            payload: updatedPredicateDevices,
          });
        })
        .catch((err) => {
          NotificationToaster({
            message: 'Predicate Device status could not be confirmed',
            type: 'error',
          });
        });
    } else if (productStatus === 'CONFIRMED') {
      unconfirmPredicateDevice({
        variables: {
          ideaId: ideaId,
          productId: productId
        }
      })
        .then((res) => {
          const updatedPredicateDevices = predicateDevices.map((obj) => {
            return obj?.id === productId
              ? { ...obj, status: res?.data?.unconfirmProductInIdea?.status }
              : obj;
          });

          setPredicateDevices(updatedPredicateDevices);
          dispatch({
            type: 'PREDICATE_DEVICES_SUCCESS',
            payload: updatedPredicateDevices,
          });
        })
        .catch((err) => {
          NotificationToaster({
            message: 'Predicate Device status could not be unconfirmed',
            type: 'error',
          });
        });
    }
  };

  // Handle removing a predicate device
  const handleRemovePredicateDevice = (row) => {
    removePredicateDevice({
      variables: {
        ideaId: ideaId,
        productId: row?.id
      }
    })
      .then((res) => {
        const updatedPredicateDevices = predicateDevices.filter(
          (obj) => obj?.id !== row?.id
        );

        setPredicateDevices(updatedPredicateDevices);
        dispatch({
          type: 'PREDICATE_DEVICES_SUCCESS',
          payload: updatedPredicateDevices,
        });

        NotificationToaster({
          message: 'Predicate Device removed successfully',
          type: 'success',
        });
      })
      .catch((err) => {
        NotificationToaster({
          message: 'Predicate Device could not be removed',
          type: 'error',
        });
      });
  };

  // Fetch predicate devices data
  const fetchPredicateDevicesData = () => {
    if (!ideaId) return;
    
    setLoading(true);
    getPredicateDevices({
      variables: {
        ideaId: ideaId
      }
    })
      .then((res) => {
        // Fix: Access the correct path in the response data structure
        const data = res?.data?.idea?.products || [];
        // console.log('Predicate devices data:', data); // Debug log
        setPredicateDevices(data);
        dispatch({
          type: 'PREDICATE_DEVICES_SUCCESS',
          payload: data,
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching predicate devices:', err);
        NotificationToaster({
          message: err.message || 'Error fetching predicate devices',
          type: 'error',
        });
        setLoading(false);
      });
  };

  // Fetch data on component mount and when ideaId changes
  useEffect(() => {
    fetchPredicateDevicesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideaId]);
  
  // Alternative approach would be to use useCallback for fetchPredicateDevicesData
  // but this is simpler for now

  // Row definitions - these represent the attributes that will be displayed for each device
  const rows = [
    { field: 'id', label: 'ID' },
    { field: 'manufacturer', label: 'Manufacturer', accessor: (device, _) => device?.manufacturer?.name || '---' },
    { field: 'name', label: 'Name' },
    { field: 'productCode', label: 'Product Code', accessor: (device, _) => device?.productCode?.code || '---' },
    { field: 'deviceClass', label: 'Device Class', accessor: (device, _) => device?.productCode?.deviceClassCode || '---' }
  ];

  // Get all unique attribute names from all devices
  const getAllAttributes = () => {
    const allAttributes = new Set();
    predicateDevices.forEach(device => {
      device?.attributes?.forEach(attribute => {
        const attrKey = `${attribute.component}_${attribute.attribute}`;
        allAttributes.add(attrKey);
      });
    });
    return Array.from(allAttributes);
  };

  // Add attributes rows to the grid
  const attributes = getAllAttributes();
  attributes.forEach(attr => {
    const [component, attribute] = attr.split('_');
    rows.push({
      field: attr,
      label: `${component} - ${attribute}`,
      accessor: (device, attributeField) => {
        const matchingAttr = device?.attributes?.find(deviceAttr => 
          `${deviceAttr.component}_${deviceAttr.attribute}` === attributeField
        );
        return matchingAttr?.value || '---';
      }
    });
  });

  // Add status row after attributes
  rows.push({
    field: 'status',
    label: 'Status',
    isStatus: true
  });

  // Sort predicate devices based on the selected field and direction
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if already sorting by this field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted predicate devices
  const getSortedPredicateDevices = () => {
    if (!sortField) return predicateDevices;
    
    return [...predicateDevices].sort((a, b) => {
      let valueA, valueB;
      
      // Get the values to compare based on the field
      const row = rows.find(r => r.field === sortField);
      if (row?.accessor) {
        valueA = row.accessor(a, row.field) || '';
        valueB = row.accessor(b, row.field) || '';
      } else if (sortField === 'id') {
        valueA = a.id || '';
        valueB = b.id || '';
      } else if (sortField === 'name') {
        valueA = a.name || '';
        valueB = b.name || '';
      } else if (sortField === 'status') {
        valueA = a.status || '';
        valueB = b.status || '';
      } else {
        valueA = '';
        valueB = '';
      }
      
      // Convert to lowercase strings for comparison
      valueA = String(valueA).toLowerCase();
      valueB = String(valueB).toLowerCase();
      
      // Compare based on direction
      if (sortDirection === 'asc') {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    });
  };

  // Get the value to display for a specific attribute of a device
  const getAttributeValue = (device, attribute) => {
    console.log('getAttributeValue called:', {
      deviceId: device?.id,
      attributeField: attribute.field,
      hasAttributes: !!device?.attributes,
      attributesCount: device?.attributes?.length
    });

    if (!device) return '---';
    
    if (attribute.accessor) {
      console.log('Using accessor for field:', attribute.field);
      return attribute.accessor(device, attribute.field);
    }
    
    if (attribute.field === 'id') return device.id || '---';
    if (attribute.field === 'name') return device.name || '---';
    
    // Look for the attribute in attributes
    console.log('Looking for attribute:', attribute.field);
    console.log('Attributes array structure:', device?.attributes?.[0]);
    const matchingAttr = device?.attributes?.find(attr => {
      // The attribute field in our UI uses underscores, but the API uses spaces
      const attrKey = `${attr.component}_${attr.attribute.replace(/ /g, '_')}`;
      console.log('Checking attribute:', {
        component: attr.component,
        attribute: attr.attribute,
        value: attr.value,
        key: attrKey,
        matches: attrKey === attribute.field
      });
      return attrKey === attribute.field;
    });
    
    console.log('Found matching attribute:', matchingAttr);
    return matchingAttr?.value || '---';
  };

  return (
    <StyledPaper>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#038C5A' }}>
        Predicate Devices
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : predicateDevices.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No predicate devices found. Add devices to see them here.
          </Typography>
        </Box>
      ) : (
        <div style={{ marginTop: '0', overflowX: 'auto', width: '100%', whiteSpace: 'nowrap', padding: '0', border: '1px solid #e0e0e0' }}>
          {/* First column with attribute labels - sticky position */}
          <Box 
            sx={{ 
              minWidth: '120px', 
              maxWidth: '120px',
              display: 'inline-block', 
              verticalAlign: 'top',
              position: 'sticky',
              left: 0,
              zIndex: 2,
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRight: '1px solid #e0e0e0'
            }}
          >
            {/* Attribute labels */}
            {rows.map((row) => (
              <HeaderCell 
                key={row.field} 
                sx={{ 
                  height: '60px',
                  display: 'flex',
                  alignItems: row.field.includes('_') ? 'flex-start' : 'center',
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)',
                  backgroundColor: row.field === 'status' ? '#e6f4ea' : '#f5f5f5'
                }}
              >
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleSort(row.field)}
                    sx={{ 
                      position: 'absolute',
                      top: '-5px',
                      right: '-4px',
                      transform: 'none',
                      color: sortField === row.field ? '#038C5A' : 'action.disabled',
                      padding: '1px'
                    }}
                  >
                    {sortField === row.field ? (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    ) : (
                      <SortIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Box sx={{ mt: 2, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    {row.label.includes(' - ') ? (
                      <>
                        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', fontSize: '0.85rem', textAlign: 'center', mb: 0.5 }}>
                          {row.label.split(' - ')[1]}
                        </Typography>
                        <Typography variant="caption" sx={{ wordBreak: 'break-word', fontSize: '0.7rem', textAlign: 'center', color: 'text.secondary' }}>
                          ({row.label.split(' - ')[0]})
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', fontSize: '0.85rem', textAlign: 'center' }}>
                        {row.label}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </HeaderCell>
            ))}
          </Box>
          
          {/* Device columns */}
          {getSortedPredicateDevices().map((device, deviceIndex) => (
            <Box 
              key={device?.id || deviceIndex} 
              sx={{ 
                minWidth: '200px', 
                maxWidth: '200px',
                display: 'inline-block',
                verticalAlign: 'top',
                borderRight: '1px solid #e0e0e0'
              }}
            >
              {/* Device attribute values */}
              {rows.map((row, rowIndex) => (
                <DataCell key={`${device?.id}-${row.field}`} sx={{ height: '60px', position: rowIndex === 0 ? 'relative' : 'static' }}>
                  {row.isStatus ? (
                    <StatusButton 
                      variant="contained" 
                      status={device?.status}
                      onClick={() => handleStatusChange(device?.id, device?.status)}
                    >
                      {device?.status === 'NEW_CHANGES' ? 'CONFIRM' : (device?.status || '---')}
                    </StatusButton>
                  ) : row.isAction ? (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>-</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' /* Allow Box to fill DataCell's height */ }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', width: '100%', padding: '0 16px', maxWidth: '100%', textAlign: 'center', fontSize: '0.8rem' }}>{getAttributeValue(device, row)}</Typography>
                      {rowIndex === 0 && (
                        <Tooltip title="Remove device">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleRemovePredicateDevice(device)}
                            sx={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </DataCell>
              ))}
            </Box>
          ))}
        </div>
      )}
    </StyledPaper>
  );
};

export default PredicatesGrid;
